<template>
  <sticky-header-layout>
    <template #header>
      <div class="action-container">
        <div class="pagination-container">
          <el-pagination
            :disabled="isLoading"
            :current-page="listQuery.page"
            :page-sizes="listQuery.sizes"
            :page-size="listQuery.size"
            :total="listQuery.total"
            layout="total, sizes, pager, jumper"
            background
            size="mini"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
        <div class="button-container">
          <el-button
            :disabled="isLoading"
            icon="el-icon-close"
            size="mini"
            @click="resetFiliter"
          >{{ $t('button.resetFilter') }}</el-button>
          <el-button
            :disabled="isLoading"
            type="primary"
            icon="el-icon-search"
            size="mini"
            @click="handleSearch"
          >{{ $t('button.search') }}</el-button>
          <el-button
            :disabled="isLoading"
            type="primary"
            class="mb-2"
            @click="handleExportCSV"
          >
            {{ $t('button.generateCSV') }}
          </el-button>
        </div>
      </div>
      <div class="filter-container">
        <div class="filter-item">
          <label-wrapper label="Payment Status">
            <gx-select
              :searchable="false"
              :disabled="isLoading"
              v-model="filter.payment_status"
              :options="paymentStatusList"
              track-by="value"
              label="label"
            >
              <template #selectedLabel="{ option }">{{ option.label }}</template>
              <template #option="{ option }">{{ option.label }}</template>
            </gx-select>
          </label-wrapper>
        </div>
        <div class="filter-item">
          <label-wrapper label="Merchant">
            <el-select
              v-model="filter.merchant_id"
              :disabled="isLoading"
              :loading="isFetchingMerchants"
              filterable
            >
              <el-option
                v-for="item in merchants"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </label-wrapper>
        </div>
        <div class="filter-item">
          <label-wrapper label="Order Number">
            <el-input
              :disabled="isLoading"
              v-model="filter.order_number"
              clearable
            />
          </label-wrapper>
        </div>

        <div class="filter-item">
          <label-wrapper :label="$t('mpOrder.dateRange')">
            <gx-date-range-select
              :date-range.sync="filter.date_range"
              :disabled="isLoading"
              show-date-range
              @selectedDateRange="handleSelectedDateRange"
            />
          </label-wrapper>
        </div>
      </div>
    </template>

    <el-table
      v-loading="isLoading"
      :data="list"
      stripe
      border
    >
      <el-table-column
        v-for="{ type, ...column } in mpOrderColumnList"
        :key="type"
        :prop="type"
        :label="column.title || $t(`table.${type}`)"
        v-bind="column.custom"
      >
        <template #default="{ row }">
          <template v-if="type === 'shipment.shipment_status'">
            {{ row.shipment.shipment_status }}
          </template>
          <template v-else-if="type === 'shipment.tracking_url'">
            <template v-if="row.shipment.tracking_url || row.shipment.tracking_number">
              <a
                v-if="row.shipment.tracking_url"
                :href="row.shipment.tracking_url"
                target="_blank"
              >
                {{ row.shipment.tracking_number || row.shipment.tracking_url }}
              </a>
              <span v-else>
                {{ row.shipment.tracking_number || row.shipment.tracking_url }}
              </span>
            </template>
            <template v-else>/</template>
          </template>
          <template v-else-if="type === 'shipment.tracking_documents'">
            <template v-if="row.shipment.tracking_documents">
              <div v-for="documents in row.shipment.tracking_documents">
                <a
                  :href="documents"
                  target="_blank"
                >
                  View
                </a>
              </div>
            </template>
            <template v-else>/</template>
          </template>
          <template v-else>
            {{ row[type] }}
          </template>
        </template>
      </el-table-column>

      <el-table-column
        fixed="right"
        label="Action"
        align="center"
        min-width="100"
      >
        <template slot-scope="scope">
          <el-button
            type="info"
            icon="el-icon-view"
            circle
            @click="showOrderDetail(scope.row)"
          />
          <el-button
            v-if="scope.row.payment_status === 'Pending Review'"
            type="success"
            size="mini"
            icon="el-icon-success"
            circle
            @click="handleApproveOrder(scope.row)"
          />
        </template>
      </el-table-column>
    </el-table>
    <el-dialog
      :visible.sync="showOrderDetailModal"
      title="Order Detail"
    >
      <div>Order Number: {{ selectedOrderNumber }}</div>
      <div>Email: {{ selectedOrderMemberEmail }}</div>
      <div style="margin-top: 10px; margin-bottom: 10px">
        <el-button
          :disabled="isLoading"
          type="primary"
          size="mini"
          @click="showFullRefund"
        >{{ $t('button.requestFullRefund') }}</el-button>
      </div>
      <el-table :data="selectedOrderDetail">
        <el-table-column
          v-for="{ type, ...column } in mpOrderDetailColumnList"
          :key="type"
          :prop="type"
          :label="column.title || $t(`table.${type}`)"
          v-bind="column.custom"
        />
        <el-table-column
          fixed="right"
          label="Action"
          align="center"
          min-width="150"
        >
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.product_type_count > 1 || scope.row.quantity > 1"
              type="primary"
              size="mini"
              @click="showPartialRefund(scope.row)"
            >{{ $t('button.requestRefund') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>
    <el-dialog
      :visible.sync="showPartialRefundReason"
      title="Refund Reason"
    >
      <el-table-column
        label="Reason"
        align="center"
      >
        Refund Reason:
        <el-select
          v-model="partialRefundForm.reason_id"
          filterable
        >
          <el-option
            v-for="item in refundReasonList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-table-column>
      <el-table-column
        label="Reason"
        align="center"
        :style="{ 'margin-top': '10px' }"
      >
        Refund Quantity: {{ selectedPartialRefundQuantity }}
        <el-button
          type="primary"
          size="mini"
          :style="{ 'position': 'relative', 'float': 'right'}"
          @click="handleRequestPartialRefund()"
        >{{ $t('button.confirmRefundRequest') }}</el-button>
      </el-table-column>
    </el-dialog>
    <el-dialog
      :visible.sync="showRefundReason"
      title="Refund Reason"
    >
      <el-table-column
        label="Reason"
        align="center"
      >
        Refund Reason:
        <el-select
          v-model="fullRefundForm.reason_id"
          filterable
        >
          <el-option
            v-for="item in refundReasonList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
        <el-button
          type="primary"
          size="mini"
          :style="{ 'position': 'relative', 'float': 'right'}"
          @click="handleRequestFullRefund()"
        >{{ $t('button.confirmRefundRequest') }}</el-button>
      </el-table-column> 
    </el-dialog>
    <generate-csv-dialog
      :visible="isGenerateCsvDialogVisible"
      @close="isGenerateCsvDialogVisible = false"
      @generate-csv="handleGenerateCsv"
    />
  </sticky-header-layout>
</template>

<script>
import format from 'date-fns/format'

import LabelWrapper from '../../components/form/LabelWrapper.vue'
import GenerateCsvDialog from './components/GenerateCsvDialog.vue'

import MarketplaceService from '../../api/marketplace'
import RefundService from '../../api/refund'
import useTablePaging from '../../mixins/useTablePaging'
import { getUTCDate } from '../../utils/helper'
import fileDownload from '../../utils/file-download'
import {
  mpOrderColumnList,
  mpOrderDetailColumnList,
  paymentStatusList,
} from '../../const/marketplace/orders'

export default {
  name: 'Orders',
  components: {
    LabelWrapper,
    GenerateCsvDialog,
  },
  mixins: [useTablePaging],
  data() {
    return {
      filter: this.getDefaultFilter(),
      previousPayload: null,
      isLoading: false,

      showOrderDetailModal: false,
      isGenerateCsvDialogVisible: false,
      selectedOrderNumber: null,
      selectedOrderMemberEmail: null,
      selectedOrderDetail: null,
      showRefundReason: false,
      showPartialRefundReason: false,
      selectedRefundReason: false,
      selectedPartialRefundReason: null,
      selectedPartialRefundQuantity: null,
      selectedPartialRefundOrderId: null,
      isFetchingMerchants: false,
      merchants: [],

      paymentStatusList,
      mpOrderColumnList,
      mpOrderDetailColumnList,
      refundReasonList: [],
      selectedFullRefundReasonId: '',
      selectedPartialRefundReasonId: '',

      fullRefundForm: {
        type: 'full',
        reason_id: null,
        remarks: null,
      },
      partialRefundForm: {
        type: 'partial',
        reason_id: null,
        remarks: null,
        order_product_id: null,
        refund_quantity: null,
      },
    }
  },
  computed: {
    canExportCSV() {
      return this.list.length > 0
    },
  },
  mounted() {
    this.fetchMerchants()
    this.getRefundList()
  },
  methods: {
    async fetchMerchants() {
      this.isFetchingMerchants = true
      try {
        const response = await MarketplaceService.getMerchantList()
        this.merchants = [
          { id: '', name: this.$t('mpOrder.all') },
          ...response.data,
        ]
      } finally {
        this.isFetchingMerchants = false
      }
    },
    getDefaultFilter() {
      return {
        payment_status: { value: '', label: 'All' },
        order_number: '',
        merchant_id: '',
        date_range: null,
        customDateRange: [],
      }
    },
    getPayload() {
      return {
        paymentStatusId: this.filter.payment_status.value,
        orderNumber: this.filter.order_number.length > 0 ? this.filter.order_number : null,
        merchantId: this.filter.merchant_id,
        fromDate: this.filter.customDateRange.length > 0 ? format(getUTCDate(this.filter.customDateRange[0]), 'yyyy-MM-dd') : null,
        toDate: this.filter.customDateRange.length > 0 ? format(getUTCDate(this.filter.customDateRange[1]), 'yyyy-MM-dd') : null,
      }
    },
    async getList() {
      this.isLoading = true
      const payload = this.getPayload()
      const page = JSON.stringify(this.previousPayload) !== JSON.stringify(payload)
        ? 1
        : this.listQuery.page

      this.previousPayload = payload
      try {
        const response = await MarketplaceService.getOrders(payload, page, this.listQuery.size)
        const { data: newList, pagination } = response
        this.list = []
        if (Array.isArray(newList)) {
          this.list = newList
        }
        this.listQuery.total = parseInt(pagination.total_items, 0)
        this.listQuery.page = page
      } catch (error) {
        this.list = []
      } finally {
        this.isLoading = false
      }
    },
    async getRefundList() {
      const res = await RefundService.getRefundList()
      this.refundReasonList = res.data
    },
    showOrderDetail(record) {
      this.selectedOrderNumber = record.order_number
      this.selectedOrderMemberEmail = record.member_email
      this.selectedOrderDetail = record.products
      for (let i = 0; i < this.selectedOrderDetail.length; i++) {
        this.selectedOrderDetail[i]['product_type_count'] = this.selectedOrderDetail.length
      }
      this.selected = record.refund_reason
      this.fullRefundForm.remarks = record.remarks || ''
      this.$nextTick(() => {
        this.showOrderDetailModal = true
      })
    },
    showFullRefund() {
      this.fullRefundForm.reason_id = null

      this.$nextTick(() => {
        this.showRefundReason = true
      })
    },
    showPartialRefund(details) {
      this.partialRefundForm.reason_id = null

      this.selectedPartialRefundReason = details.refund_reason
      this.selectedPartialRefundQuantity = details.quantity
      this.selectedPartialRefundOrderId = details.order_number
      this.partialRefundForm.order_product_id = details.order_product_id
      this.partialRefundForm.refund_quantity = details.quantity
      this.partialRefundForm.remarks = details.remarks || ''
      this.$nextTick(() => {
        this.showPartialRefundReason = true
      })
    },
    handleSearch() {
      this.getList()
    },
    resetFiliter() {
      this.filter = this.getDefaultFilter()
    },
    async approveOrder(orderNumber) {
      this.isLoading = true

      try {
        await MarketplaceService.approveOrder(orderNumber)
        this.isLoading = false
        this.getList()
      } catch (error) {
        this.isLoading = false
      }
    },
    handleApproveOrder(row) {
      return this.$confirm(
        `Do you want to approve the order (${row.order_number}) now?`,
        'Confirm Approve', {
          confirmButtonText: 'Confirm',
          cancelButtonText: 'Cancel',
          type: 'info',
        },
      ).then(() => {
        this.approveOrder(row.order_number)
      }).catch(() => {
      })
    },
    handleSelectedDateRange(dateRange) {
      this.filter.customDateRange = dateRange
    },
    handleExportCSV() {
      this.isGenerateCsvDialogVisible = true
    },
    async handleGenerateCsv({ type: exportReportType, onSuccess }) {
      try {
        const { data } = await MarketplaceService.downloadOrders({
          ...this.getPayload(),
          exportReportType,
          exportCsv: 'true',
        })
        fileDownload(data, 'marketplace_orders.csv')

        onSuccess()
      } catch (err) {
        console.log(err)
      }
    },
    async handleRequestFullRefund() {
      try {
        await RefundService.createRefundRequest({form: this.fullRefundForm, orderId: this.selectedOrderNumber})
        this.showRefundReason = false
        this.showOrderDetailModal = false
      } catch (e) {
        console.log(e)
      }
    },
    async handleRequestPartialRefund() {
      try {
        await RefundService.createRefundRequest({form: this.partialRefundForm, orderId: this.selectedOrderNumber})
        this.showPartialRefundReason = false
        this.showOrderDetailModal = false
      } catch (e) {
        console.log(e)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";

.filter-container {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  flex-grow: 1;
  .filter-title {
    font-size: 13px;
    margin-right: 5px;
  }
}

section {
  .filter-item {
    margin: 3px 0;
    width: 100%;
    @include mobile {
      margin: 3px 5px;
      width: calc(100% / 3 - 10px);
    }
    @include tablet {
      width: calc(100% / 4 - 10px);
    }
    @include desktop {
      width: calc(100% / 6 - 10px);
    }
  }
  .data-picker-container {
    position: relative;
    // Make the hack button span the whole parent container.
    .absolute-view {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
    }
  }
}

.data-picker-container {
  width: 200px;
}

::v-deep {
  .el-table__row {
    .cell {
      a {
        text-decoration: underline;
      }
    }
  }
}

</style>
